import { type CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * Cap on how many toasts stay open at once. Every open toast keeps a live timer and its own `aria-live`
 * region, so a burst of toggles would otherwise grow the DOM and the screen-reader queue without limit.
 */
export const MAX_STACKED_TOASTS = 10;

const VISIBLE_DEPTH = 3;

export type ToastStack = {
  /**
   * Per-notification values, in the same order as the entries handed in.
   */
  cards: ToastStackCard[];
  /**
   * Whether the stack is fanned out. Cards read it to swap their collapsed clamp for their own height.
   */
  isExpanded: boolean;
  /**
   * Whether focus is on a toast itself. Narrower than `isExpanded`: focus resting on the viewport
   * expands the stack without counting as a reader on any one card.
   */
  isFocused: boolean;
  /**
   * Whether the pointer is over the stack.
   */
  isHovered: boolean;
  /**
   * Ref for the toast viewport, whose pointer and focus traffic drives `isExpanded`.
   */
  viewportRef: (element: HTMLElement | null) => void;
};

export type ToastStackCard = {
  /**
   * Ref for the card's content wrapper, inside the clamped root, so it measures the natural height.
   */
  contentRef: (element: HTMLElement | null) => void;
  /**
   * The notification this card belongs to.
   */
  id: number;
  /**
   * Whether this is the front card, the only one shown in full while the stack is collapsed.
   */
  isFront: boolean;
  /**
   * Whether the card is shown at all. False past the visible depth while collapsed, so focus skips
   * those cards. A closing card is always shown, so it cannot blink out part-way through its exit.
   */
  isVisible: boolean;
  /**
   * Custom properties driving the card's transform, height and paint order.
   */
  style: CSSProperties;
};

export type ToastStackEntry = {
  /**
   * The notification this entry belongs to.
   */
  id: number;
  /**
   * Whether the toast is still open. A closing one leaves the layout at once, not after its exit.
   */
  isOpen: boolean;
};

export type ToastStackOrder = 'newest-first' | 'oldest-first';

type ToastStackOptions = {
  /**
   * Which end of the fan the newest toast sits at. Defaults to 'newest-first'.
   */
  stackOrder?: ToastStackOrder;
};

/**
 * Turns a list of live notifications into a collapsed stack of cards — the front one in full, the
 * rest peeking behind it — that fans out on hover or focus.
 *
 * Owns the measured height of every card, which has to be measured rather than assumed because a toast
 * grows with its message and a collapsed card clamps to the front card's height.
 * @param entries - the live notifications, oldest first, matching the order they render in
 * @param options - which end of the fan the newest toast sits at
 */
const useToastStack = (
  entries: ToastStackEntry[],
  { stackOrder = 'newest-first' }: ToastStackOptions = {}
): ToastStack => {
  const [heights, setHeights] = useState<Record<number, number>>({});
  const [isFocusWithin, setIsFocusWithin] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [viewport, setViewport] = useState<HTMLElement | null>(null);

  const isExpanded = isFocusWithin || isHovered;

  /*
   * State rather than refs: the `cards` memo below calls `getContentRef`, which reaches all of these,
   * and refs must not be read from render. Each identity stays stable across renders (nothing ever
   * calls its setter) — only contents are mutated imperatively via Map methods, same as a ref would be.
   * `observerBox` is a single-entry Map standing in for a mutable box, since reassigning a plain
   * object's property directly would count as modifying a value returned from `useState`.
   */
  const [observerBox] = useState(() => new Map<'observer', ResizeObserver>());
  const [elementsById] = useState(() => new Map<number, HTMLElement>());
  const [idsByElement] = useState(() => new WeakMap<Element, number>());
  const [contentRefs] = useState(() => new Map<number, (element: HTMLElement | null) => void>());

  /*
   * One observer for the whole stack rather than one per card, following `TruncationObserverManager`.
   * Not deferred to `requestAnimationFrame` the way that manager is: a resize callback already runs
   * after layout, and a frame's delay would lay the first card out against an unknown height.
   */
  const getObserver = useCallback(() => {
    const observer =
      observerBox.get('observer') ??
      new ResizeObserver(entries => {
        const measured = new Map<number, number>();

        for (const entry of entries) {
          const id = idsByElement.get(entry.target);
          if (id !== undefined && entry.target instanceof HTMLElement && entry.target.offsetHeight > 0) {
            measured.set(id, entry.target.offsetHeight);
          }
        }

        // Bail when nothing moved: writing state unconditionally from a resize callback loops.
        setHeights(previous => {
          let hasChanged = false;
          const next = { ...previous };

          for (const [id, height] of measured) {
            if (next[id] === height) {
              continue;
            }

            next[id] = height;
            hasChanged = true;
          }

          return hasChanged ? next : previous;
        });
      });

    observerBox.set('observer', observer);

    return observer;
  }, [observerBox, idsByElement]);

  useEffect(() => () => observerBox.get('observer')?.disconnect(), [observerBox]);

  // Cached per notification, so a re-render hands React the same function and re-observes nothing.
  const getContentRef = useCallback(
    (id: number) => {
      const cached = contentRefs.get(id);
      if (cached) {
        return cached;
      }

      const contentRef = (element: HTMLElement | null) => {
        const observer = getObserver();
        const previous = elementsById.get(id);

        if (previous) {
          observer.unobserve(previous);
          elementsById.delete(id);
        }

        /*
         * A detach forgets the element and nothing more. Dropping the cached callback or the measured
         * height would hand React a fresh callback next render, which it detaches again — the two never
         * settle, and the heights are wiped as fast as they are taken. Departures are pruned below.
         */
        if (!element) {
          return;
        }

        elementsById.set(id, element);
        idsByElement.set(element, id);
        observer.observe(element);
      };

      contentRefs.set(id, contentRef);

      return contentRef;
    },
    [getObserver, contentRefs, elementsById, idsByElement]
  );

  const liveIds = useMemo(() => new Set(entries.map(({ id }) => id)), [entries]);
  /*
   * A value key for `liveIds`, stable across renders that carry the same ids even when callers hand
   * in a fresh `entries` array every render (as `renderHook` does in tests).
   */
  const liveIdsKey = [...liveIds].toSorted((a, b) => a - b).join(',');

  /*
   * Forgets the notifications that have gone. Keyed on the entries, so a re-attach is never mistaken
   * for a departure and a closing toast keeps its height until it is really removed.
   */
  useEffect(() => {
    for (const id of contentRefs.keys()) {
      if (liveIds.has(id)) {
        continue;
      }

      contentRefs.delete(id);
      elementsById.delete(id);
    }
  }, [liveIds, contentRefs, elementsById]);

  /*
   * Stale heights would leave a card that reuses a departed id inheriting the wrong measurement until
   * it's freshly observed. Pruned during render (see
   * https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes)
   * rather than an effect, since it's purely derived from the live ids.
   */
  const [prevLiveIdsKey, setPrevLiveIdsKey] = useState(liveIdsKey);

  if (liveIdsKey !== prevLiveIdsKey) {
    setPrevLiveIdsKey(liveIdsKey);

    setHeights(previous => {
      const staleIds = Object.keys(previous).filter(id => !liveIds.has(Number(id)));
      if (staleIds.length === 0) {
        return previous;
      }

      const next = { ...previous };
      for (const id of staleIds) {
        delete next[Number(id)];
      }

      return next;
    });
  }

  // An emptied stack must not greet the next toast already fanned out. Idempotent, so safe on every render.
  if (entries.length === 0 && (isFocusWithin || isFocused || isHovered)) {
    setIsFocusWithin(false);
    setIsFocused(false);
    setIsHovered(false);
  }

  const cards = useMemo(() => {
    /*
     * Walked nearest the pinned edge first, the direction depth and offset accumulate in. DOM order
     * stays oldest-first for Radix's keyboard traversal, so a newest-first stack reads it backwards.
     */
    const anchorOrder = stackOrder === 'newest-first' ? [...entries].toReversed() : entries;

    const depths = new Map<number, number>();
    const offsets = new Map<number, number>();
    let frontOpenId: number | undefined;
    let depth = 0;
    let offset = 0;

    /*
     * A closing card keeps the slot it was resting in but does not advance the counters, which is what
     * overlaps its exit with the reflow: the cards behind take its space on the render it starts
     * closing. It shares depth 0 with whichever card is promoted in front of it.
     */
    for (const entry of anchorOrder) {
      depths.set(entry.id, depth);
      offsets.set(entry.id, offset);

      if (!entry.isOpen) {
        continue;
      }

      frontOpenId ??= entry.id;
      depth += 1;
      offset += heights[entry.id] ?? 0;
    }

    const frontHeight = frontOpenId === undefined ? undefined : heights[frontOpenId];

    return entries.map(({ id, isOpen }) => {
      const cardDepth = depths.get(id) ?? 0;
      const ownHeight = heights[id];

      return {
        contentRef: getContentRef(id),
        id,
        isFront: cardDepth === 0,
        isVisible: !isOpen || isExpanded || cardDepth < VISIBLE_DEPTH,
        /*
         * Heights are omitted until measured so the CSS `auto` fallback applies; `0px` would flatten
         * the card on its first frame. `--offset` carries only the summed heights, not the gap.
         */
        style: {
          ...(frontHeight !== undefined && { '--front-toast-height': `${frontHeight}px` }),
          ...(ownHeight !== undefined && { '--own-height': `${ownHeight}px` }),
          '--index': String(cardDepth),
          '--offset': `${offsets.get(id) ?? 0}px`,
          '--z-index': String((entries.length - cardDepth) * 2 + (isOpen ? 0 : 1)),
        } as CSSProperties,
      };
    });
  }, [entries, getContentRef, heights, isExpanded, stackOrder]);

  /*
   * Native listeners rather than React handlers: a card is the viewport's DOM child but its React
   * *sibling* — Radix portals each one in — so a synthetic `onPointerMove` here would never see them.
   */
  useEffect(() => {
    if (!viewport) {
      return;
    }

    const expandOnPointer = () => setIsHovered(true);
    const collapseOnPointer = () => setIsHovered(false);

    /*
     * Radix hands the viewport itself focus when it closes a toast that was holding it. That is
     * teardown, and no `focusout` follows to undo it, so counting it as a reader would strand every
     * surviving toast paused. The stack still opens for it, which is how F8 reaches the buried cards.
     */
    const expandOnFocus = ({ target }: FocusEvent) => {
      setIsFocusWithin(true);
      setIsFocused(target !== viewport);
    };

    // Focus moving from one card to the next fires focusout before focusin; collapsing on that flickers.
    const collapseOnFocus = ({ relatedTarget }: FocusEvent) => {
      if (relatedTarget instanceof Node && viewport.contains(relatedTarget)) {
        return;
      }

      setIsFocusWithin(false);
      setIsFocused(false);
    };

    /*
     * `pointermove` rather than `pointerenter`: no boundary event fires for a card that materialises
     * under a resting pointer. `pointerleave` is safe — boundary events run the hit target's ancestors.
     */
    viewport.addEventListener('focusin', expandOnFocus);
    viewport.addEventListener('focusout', collapseOnFocus);
    viewport.addEventListener('pointerleave', collapseOnPointer);
    viewport.addEventListener('pointermove', expandOnPointer);

    return () => {
      viewport.removeEventListener('focusin', expandOnFocus);
      viewport.removeEventListener('focusout', collapseOnFocus);
      viewport.removeEventListener('pointerleave', collapseOnPointer);
      viewport.removeEventListener('pointermove', expandOnPointer);
    };
  }, [viewport]);

  return { cards, isExpanded, isFocused, isHovered, viewportRef: setViewport };
};

export default useToastStack;
